import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../store/authStore';
import { ROUTES } from '../config/constants';
import toast from 'react-hot-toast';
import { setUserContext, clearUserContext, addBreadcrumb } from '../lib/sentry';
import { clearUserScopedStorage } from '../lib/sessionCleanup';

export const useAuth = () => {
  const { user, isLoading, isAuthenticated, setUser: _setUser, setLoading, logout: storeLogout } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const signUp = async (email: string, password: string, displayName: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            display_name: displayName,
          },
        },
      });

      if (error) throw error;

      if (data.user) {
        // Set user context in Sentry
        setUserContext({
          id: data.user.id,
          email: data.user.email,
          username: data.user.user_metadata?.display_name,
        });
        // Add breadcrumb for tracking
        addBreadcrumb('User signed up', 'auth', {
          userId: data.user.id,
          email: data.user.email,
        });
        toast.success('Account created successfully!');
        return { success: true, user: data.user };
      }

      return { success: false, error: 'Failed to create account' };
    } catch (error: any) {
      toast.error(error.message || 'Failed to sign up');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        // Set user context in Sentry
        setUserContext({
          id: data.user.id,
          email: data.user.email,
          username: data.user.user_metadata?.display_name,
        });
        // Add breadcrumb for tracking
        addBreadcrumb('User signed in', 'auth', {
          userId: data.user.id,
          email: data.user.email,
        });
        toast.success('Logged in successfully!');
        navigate(ROUTES.DASHBOARD);
        return { success: true, user: data.user };
      }

      return { success: false, error: 'Failed to log in' };
    } catch (error: any) {
      toast.error(error.message || 'Failed to log in');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signOut();

      if (error) throw error;

      // Clear all cached data
      queryClient.clear();
      clearUserScopedStorage();

      // Add breadcrumb before clearing context
      addBreadcrumb('User logged out', 'auth');
      
      // Clear Sentry user context
      clearUserContext();
      
      storeLogout();
      toast.success('Logged out successfully');
      navigate(ROUTES.HOME);
    } catch (error: any) {
      toast.error(error.message || 'Failed to log out');
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      toast.success('Password reset email sent!');
      return { success: true };
    } catch (error: any) {
      toast.error(error.message || 'Failed to send reset email');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  return {
    user,
    isLoading,
    isAuthenticated,
    signUp,
    signIn,
    logout,
    resetPassword,
  };
};
